import React from 'react';

import { 
  Cloud, 
  Users, 
  BarChart3, 
  Zap, 
  Shield, 
  Settings, 
  CheckCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SalesforceSolutions = () => {
  const capabilities = [
    {
      icon: <Cloud className="salesforce-service-icon" />,
      title: "Cloud Architecture",
      description: "Design and implement scalable cloud solutions tailored to your business needs."
    },
    {
      icon: <Settings className="salesforce-service-icon" />,
      title: "Custom Development",
      description: "Build custom applications and integrations to extend your digital capabilities."
    },
    {
      icon: <Users className="salesforce-service-icon" />,
      title: "Customer Experience",
      description: "Create seamless customer journeys with intelligent automation and personalization."
    },
    {
      icon: <BarChart3 className="salesforce-service-icon" />,
      title: "Data Analytics",
      description: "Advanced business intelligence and reporting for data-driven decisions."
    },
    {
      icon: <Zap className="salesforce-service-icon" />,
      title: "System Integration",
      description: "Connect disparate systems for unified operations and data flow."
    },
    {
      icon: <Shield className="salesforce-service-icon" />,
      title: "Security & Compliance",
      description: "Enterprise-grade security solutions to protect your data and systems."
    }
  ];

  const benefits = [
    "Improved customer satisfaction scores",
    "Reduced manual processes and errors",
    "Enhanced data visibility and reporting",
    "Faster time-to-market for new solutions",
    "Better alignment between technology and business goals"
  ];

  const testimonials = [
    {
      quote: "Every day brings new opportunities to learn, grow, and make a meaningful impact in the industry."
    },
    {
      quote: "The collaborative environment and commitment to innovation here creates the perfect conditions for professional growth and breakthrough solutions."
    },
    {
      quote: "Working with cutting-edge technologies on meaningful projects makes every day both challenging and rewarding."
    },
    {
      quote: "The culture of excellence combined with genuine teamwork makes this an exceptional place to build a career."
    },
    {
      quote: "Where passion meets purpose—delivering solutions that make a difference for businesses and communities."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Analysis",
      description: "Analyzing a current processes and identify optimization opportunities."
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Develop a comprehensive roadmap tailored to your business objectives."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Configure and customize solutions to match your specific requirements."
    },
    {
      step: "04",
      title: "Training & Support",
      description: "Comprehensive user training and ongoing support to ensure success."
    }
  ];

  const [activeTestimonial, setActiveTestimonial] = React.useState(0);

  // Auto-scroll testimonials
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change every 5 seconds
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="salesforce-solutions">
      {/* Hero Section */}
      <section className="quantum-migration-showcase">
        <div className="nexus-quantum-container">
          <div className="nexus-hero-content">
            <div className="hero-text">
            <h1 >
            Salesforce Platform Expertise
            </h1>
            <p className="salesforce-hero-subtitle">
              Transform your business with our comprehensive technology solutions. 
            </p>
            <div className="salesforce-hero-buttons">
              <button className="quantum-cta-nexus">
                Get Started Today <span className="nexus-cta-arrow">→</span>
              </button>
              {/* <button className="quantum-cta-nexus">
                Schedule Consultation
              </button> */}
            </div>
          </div>
        </div>
        </div>
        <div className="salesforce-hero-gradient"></div>
      </section>

      {/* Capabilities Grid */}
      <section className="salesforce-services">
        <div className="salesforce-services-header">
          <h2 className="salesforce-services-title">Our Capabilities</h2>
          <p className="salesforce-services-subtitle">
            Comprehensive solutions designed to maximize your digital transformation
          </p>
        </div>
        
        <div className="salesforce-services-grid">
          {capabilities.map((capability, index) => (
            <div key={index} className="salesforce-service-card">
              <div className="salesforce-service-icon-container">
                {capability.icon}
              </div>
              <h3 className="salesforce-service-name">{capability.title}</h3>
              <p className="salesforce-service-description">{capability.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="salesforce-benefits">
        <div className="salesforce-benefits-container">
          <div className="salesforce-benefits-content">
            <h2 className="salesforce-benefits-title">
              Why Choose Our Services?
            </h2>
            <p className="salesforce-benefits-description">
              Our proven methodology and deep technical expertise deliver measurable results 
              that transform your business operations and drive sustainable growth.
            </p>
            <div className="salesforce-benefits-list">
              {benefits.map((benefit, index) => (
                <div key={index} className="salesforce-benefit-item">
                  <CheckCircle className="salesforce-benefit-icon" />
                  <span className="salesforce-benefit-text">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="salesforce-stats-card">
            <div className="salesforce-stat">
              <div className="salesforce-stat-number">10+</div>
              <div className="salesforce-stat-label">Successful Projects</div>
            </div>
            <div className="salesforce-stat">
              <div className="salesforce-stat-number">98%</div>
              <div className="salesforce-stat-label">Client Satisfaction Rate</div>
            </div>
            <div className="salesforce-stat">
              <div className="salesforce-stat-number">24/7</div>
              <div className="salesforce-stat-label">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="salesforce-process">
        <div className="salesforce-process-header">
          <h2 className="salesforce-process-title">Our Implementation Process</h2>
          <p className="salesforce-process-subtitle">
            A proven methodology that ensures successful technology deployment
          </p>
        </div>
        
        <div className="salesforce-process-grid">
          {process.map((step, index) => (
            <div key={index} className="salesforce-process-step">
              <div className="salesforce-step-number">{step.step}</div>
              <h3 className="salesforce-step-name">{step.title}</h3>
              <p className="salesforce-step-description">{step.description}</p>
              {/* {index < process.length - 1 && (
                <div className="salesforce-process-connector"></div>
              )} */}
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <motion.section 
        className="testimonials-section"
        initial="hidden"
        whileInView="visible"
        variants={sectionVariants}
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container">
          <motion.h2 variants={itemVariants}>Hear From Our Team</motion.h2>
          
          <div className="testimonials-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="testimonial"
              >
                <div className="quote-icon">"</div>
                <p className="quote-text">{testimonials[activeTestimonial].quote}</p>
                <div className="author-info">
                  {/* <div className="author-avatar">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="white"/>
                      <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="white"/>
                    </svg>
                  </div> */}
                  {/* <div>
                    <div className="author-name">Team Member</div>
                    <div className="author-role">Cognifyar Professional</div>
                  </div> */}
                </div>
              </motion.div>
            </AnimatePresence>
            
            <div className="testimonial-nav">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`testimonial-dot ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(index)}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <section className="salesforce-cta">
        <div className="salesforce-cta-container">
          <h2 className="salesforce-cta-title">
            Ready to Transform Your Business?
          </h2>
          <p className="salesforce-cta-text">
            Let's discuss how our services can drive growth and efficiency for your organization.
          </p>
          <div className="salesforce-cta-buttons">
            <button className="shadow-rotating-button">
              Contact Our Experts
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesforceSolutions;